import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import Text from 'design-system/Text';
import { Button, Spacer } from 'design-system';
import { useAppState } from 'hooks';
import useColors from 'hooks/useColors';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { OnboardingFlow } from 'utils/types/navigation';
import { showContinueInMobileAlert } from 'features/pots/utils';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { updateWealthAccounts } from 'actions/invest';
import { selectOnboardingTradingAccountForOnboardingFunnel } from 'features/invest/reducers/selectors';
import ProgressCard from './ProgressCard';
import { blockInvestOnboardingOnWeb } from '../../webGate';
import { isDesktop } from '../../../../constants';
const strings = {
    completedTitle: 'Identity and address verified',
    incompleteTitle: 'Under review',
    actionRequiredTitle: 'Action required',
    desc: 'We’ll let you know once your account is approved. This process usually takes minutes',
    descSeccl: 'We’ll let you know once your account is approved. This usually takes a few hours, or up to 1 working day if submitted over the weekend.',
    actionRequiredDesc: 'We need to verify your details for regulatory purposes. Tap “verify” to provide both proofs of your identity and address.',
};
const UnderReviewProgressCard = ({ status, isLastStep }) => {
    const colors = useColors();
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const account = useAppSelector(selectOnboardingTradingAccountForOnboardingFunnel);
    const requiredUserAction = account?.account.requiredUserAction;
    // if we have rejectionString, it means that the document is rejected and we should show document reupload context
    const rejectionReasonString = account?.account?.rejections?.[0]?.rejectionReasonString;
    const completed = status === 'ACTIVE';
    const isActiveStep = status !== 'ACTIVE';
    const isActionRequired = status === 'IDENTITY_CHECKS_NEED_USER_ACTION' && !!requiredUserAction;
    const broker = account?.account.broker;
    const loadData = useCallback(async () => {
        if (!store.getState().unpersistedInvestReducer.isFetchingWealthAccounts) {
            dispatch(updateWealthAccounts());
        }
    }, []);
    useFocusEffect(useCallback(() => {
        loadData();
    }, [loadData]));
    const appStateChangeCallbacks = useMemo(() => ({
        didBecomeActive: loadData,
    }), [loadData]);
    useAppState(appStateChangeCallbacks);
    const handleVerify = useCallback(() => {
        if (blockInvestOnboardingOnWeb())
            return;
        if (isDesktop) {
            showContinueInMobileAlert();
            return;
        }
        switch (requiredUserAction) {
            case 'UPLOAD_IDENTITY_DOCUMENT':
                navigation.navigateDeprecated('WealthDocumentUploadIntroScreen', {
                    documentType: 'POI',
                    flow: OnboardingFlow.Invest,
                });
                break;
            case 'UPLOAD_PROOF_OF_ADDRESS':
                navigation.navigateDeprecated('WealthDocumentUploadIntroScreen', {
                    documentType: 'POA',
                    flow: OnboardingFlow.Invest,
                });
                break;
            default:
            // Gets here when the status is 'KYC_PENDING', but `requiredUserAction` is not set.
        }
    }, [requiredUserAction]);
    return (<ProgressCard lastItem={isLastStep} completed={completed} isActiveStep={isActiveStep} isActionRequired={isActionRequired} lightBg title={
        // eslint-disable-next-line no-nested-ternary
        isActionRequired
            ? strings.actionRequiredTitle + (rejectionReasonString ? ': Failed verification' : '')
            : completed
                ? strings.completedTitle
                : strings.incompleteTitle}>
      {status === 'IDENTITY_CHECKS_NEED_USER_ACTION' && rejectionReasonString ? (<Text TextThin-14 Secondary>
          {rejectionReasonString}
        </Text>) : (<Text TextThin-14 Secondary>
          {/* eslint-disable-next-line no-nested-ternary */}
          {isActionRequired ? strings.actionRequiredDesc : broker === 'seccl' ? strings.descSeccl : strings.desc}
        </Text>)}
      {!!isActionRequired && (<>
          <Spacer h={12}/>
          <View style={styles.row}>
            <Button hug extra custom square onPress={handleVerify} textColor={colors.text.white} containerStyle={styles.btnContainer} gradientColors={[colors.elements.statusNegative, colors.elements.statusNegative]} title={rejectionReasonString ? 'Reverify' : 'Verify'}/>
          </View>
        </>)}
    </ProgressCard>);
};
const styles = StyleSheet.create({
    row: {
        flexDirection: 'row',
    },
    btnContainer: {
        paddingHorizontal: 0,
    },
});
export default UnderReviewProgressCard;
